import React, { useEffect, useState } from 'react';
import { X, Save } from 'lucide-react';
import Modal from '@/components/ui/Modal';
import Input from '@/components/ui/Input';
import Select, { type SelectOption } from '@/components/ui/Select';
import Button from '@/components/ui/Button';
// Using standard textarea instead of TextArea component
import studiesService from '@/services/studies/StudiesService';
import { teamsService } from '@/services/teams/TeamsService';

interface StudyRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  contactId: string;
  contactName: string;
  onSave: (studyData: StudyFormData) => Promise<boolean>;
}

export interface StudyFormData {
  id?: string;
  contactId: string;
  studyType: string;
  studyDate: string;
  studyTime: string; // HH:mm
  observations: string;
  completed: boolean;
  nextStudyDate?: string;
  nextStudyTime?: string; // HH:mm
  // When true, the caller intends to also schedule the next study using nextStudyDate
  scheduleNext?: boolean;
}

const StudyRegistrationModal: React.FC<StudyRegistrationModalProps> = ({
  isOpen,
  onClose,
  contactId,
  contactName,
  onSave,
}) => {
  // Default dates: today and one week from today
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const currentTimeStr = `${pad(now.getHours())}:${pad(now.getMinutes())}`;
  const todayStr = now.toISOString().split('T')[0];
  const nextWeekStr = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0];

  const [formData, setFormData] = useState<StudyFormData>({
    contactId,
    studyType: '',
    studyDate: todayStr, // Today's date as default
    studyTime: currentTimeStr,
    observations: '',
    completed: false,
    nextStudyDate: nextWeekStr,
    nextStudyTime: currentTimeStr,
  });

  const [studyOptions, setStudyOptions] = useState<SelectOption[]>([]);
  const [studiesLoading, setStudiesLoading] = useState<boolean>(false);
  const [studiesError, setStudiesError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const fallbackStudyOptions: SelectOption[] = [
    { value: 1, label: '1 - Primeira Mensagem Angélica — O Evangelho Eterno e o Juízo' },
    { value: 2, label: '2 - Quem Devemos Adorar? — O Verdadeiro Objeto de Adoração' },
    { value: 3, label: '3 - Segunda Mensagem Angélica — A Queda de Babilônia' },
    { value: 4, label: '4 - Terceira Mensagem Angélica — O Chamado Final de Deus' },
    { value: 5, label: '5 - Como Guardar o Sábado — O Dia de Deus' },
    { value: 6, label: '6 - A Obra de Cristo no Santuário Celestial' },
    { value: 7, label: '7 - A Expiação — O Sacrifício de Cristo por Nós' },
    { value: 8, label: '8 - Profecia das 2300 Tardes e Manhãs — Daniel 8:14' },
    { value: 9, label: '9 - Estado dos Mortos — O que a Bíblia Ensina sobre a Morte' },
    { value: 10, label: '10 - Batismo em Nome de Jesus — Significado e Importância' },
    { value: 11, label: '11 - Saúde do Cristão — Corpo como Templo do Espírito Santo' },
    { value: 12, label: '12 - Vestuário e Comportamento — Aparência e Conduta' },
    { value: 13, label: '13 - Mordomia Cristã — Administrando os Dons de Deus' },
  ];

  useEffect(() => {
    if (!isOpen) return;
    let isMounted = true;
    (async () => {
      try {
        setStudiesLoading(true);
        setStudiesError('');
        // Resolve active team and adjust nextStudyDate default
        try {
          const activeTeamId = await teamsService.activeId();
          if (activeTeamId) {
            const allTeams = await teamsService.listAll();
            const me = allTeams.find(t => t.id === activeTeamId);
            const offsetDays = me?.next_schedule_days ?? 7;
            const nextDate = new Date();
            nextDate.setDate(nextDate.getDate() + (typeof offsetDays === 'number' ? offsetDays : 7));
            const nextDateStr = nextDate.toISOString().split('T')[0];
            if (isMounted) {
              setFormData(prev => ({
                ...prev,
                nextStudyDate: nextDateStr,
                // keep the time as already set (currentTimeStr)
              }));
            }
          }
        } catch {
          // fallback already set to +7 days
        }
        const res = await studiesService.getStudies();
        if (!isMounted) return;
        if (res.success) {
          const apiList = (res.data || []);
          const options: SelectOption[] = apiList.map((s) => ({
            value: String(s.id),
            label: s.title,
          }));
          if (options.length > 0) {
            setStudyOptions(options);
          } else {
            setStudiesError('Lista de estudos vazia. Exibindo lista padrão.');
            setStudyOptions(fallbackStudyOptions);
          }
        } else {
          setStudiesError(res.message || 'Não foi possível carregar os estudos. Exibindo lista padrão.');
          setStudyOptions(fallbackStudyOptions);
        }
      } catch (e) {
        if (!isMounted) return;
        setStudiesError('Erro ao carregar os estudos. Exibindo lista padrão.');
        setStudyOptions(fallbackStudyOptions);
      } finally {
        if (isMounted) setStudiesLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  const handleChange = (
    field: keyof StudyFormData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const ok = await onSave(formData);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAndSchedule = async () => {
    if (saving) return;
    if (!formData.nextStudyDate) {
      // leave validation to parent toast if preferred; still guard here
      return;
    }
    setSaving(true);
    try {
      const ok = await onSave({ ...formData, scheduleNext: true });
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Registrar Estudo">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="bg-blue-50 p-3 rounded-md mb-4">
          <p className="text-sm text-blue-800">
            <span className="font-medium">Contato:</span> {contactName}
          </p>
        </div>

        <Select
          label="Tipo de Estudo"
          value={formData.studyType}
          onChange={(e) => handleChange('studyType', e.target.value)}
          required
          options={studyOptions}
          placeholder={studiesLoading ? 'Carregando...' : 'Selecione o tipo de estudo'}
          disabled={studiesLoading}
          helperText={studiesError || undefined}
          error={studiesError || undefined}
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input
            label="Data do Estudo"
            type="date"
            value={formData.studyDate}
            onChange={(e) => handleChange('studyDate', e.target.value)}
            required
          />
          <Input
            label="Hora do Estudo"
            type="time"
            value={formData.studyTime}
            onChange={(e) => handleChange('studyTime', e.target.value)}
            required
          />

          <Input
            label="Data do Próximo Estudo"
            type="date"
            value={formData.nextStudyDate || ''}
            onChange={(e) => handleChange('nextStudyDate', e.target.value)}
          />
          <Input
            label="Hora do Próximo Estudo"
            type="time"
            value={formData.nextStudyTime || ''}
            onChange={(e) => handleChange('nextStudyTime', e.target.value)}
          />
        </div>

        <div className="w-full">
          <label className="form-label">Observações</label>
          <textarea
            className="form-input w-full"
            value={formData.observations}
            onChange={(e) => handleChange('observations', e.target.value)}
            placeholder="Anote detalhes importantes sobre o estudo, dúvidas do contato, etc."
            rows={4}
          />
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="completed"
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            checked={formData.completed}
            onChange={(e) => handleChange('completed', e.target.checked)}
          />
          <label
            htmlFor="completed"
            className="ml-2 block text-sm text-gray-700"
          >
            Estudo concluído
          </label>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={onClose}
            leftIcon={<X className="w-4 h-4" />}
            disabled={saving}
          >
            Cancelar
          </Button>
          <Button type="button" onClick={handleSaveAndSchedule} leftIcon={<Save className="w-4 h-4" />} disabled={saving || !formData.nextStudyDate}> 
            Salvar e Agendar Próximo
          </Button>
          <Button type="submit" leftIcon={<Save className="w-4 h-4" />} disabled={saving}>
            Salvar Estudo
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default StudyRegistrationModal;
